import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Compass, Sparkles, Quote, ShieldCheck, Zap, Palette, HeartHandshake, ListChecks, Smile, Bus, Clock, Sun,
  Castle, FlaskConical, BookOpen, Music, Users, Gamepad2, Hand, Brush, Library, Moon, MessagesSquare, Trophy, Leaf,
  Briefcase, Stethoscope, ShoppingBag, ListTodo, Dumbbell, Coffee, Rocket, Camera, Star, MapPin, Globe, ArrowRight,
} from "lucide-react";

// Replace null with the uploaded photo URL to show a real photo.
const IMG_HERO = null;
const IMG_STORY = null;
const IMG_PARENTS = null;

const PhotoSlot = ({ src, alt, className, label, testid }) => src ? <img src={src} alt={alt} className={`${className} object-cover`} data-testid={testid} /> : (
  <div className={`${className} bg-gradient-to-br from-turquoise/25 via-gold/20 to-pink/25 border-2 border-dashed border-turquoise/50 flex flex-col items-center justify-center text-center p-6`} data-testid={testid}>
    <Camera className="w-10 h-10 text-turquoise" />
    <p className="font-display font-bold text-navy mt-2">{label}</p>
    <p className="text-xs text-slate-500 mt-1">Real Adventure Rock photo coming soon</p>
  </div>
);

const fade = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.55 } };
const CREAM = "bg-[#FFF8EC]";
const AQUA = "bg-[#E8FAFB]";

const PARENT_NEEDS = [
  "Has to work while school ends at 3 PM",
  "Needs a safe after-school environment",
  "Needs school pick-up or home drop-off",
  "Wants their child doing more than sitting in front of a screen",
  "Needs a few hours to work, shop, attend appointments or handle life",
  "Wants somewhere their child can play while they work nearby",
  "Wants their child to have social time, physical activity and creative stimulation",
  "Simply needs a safe, happy place their child loves",
];

const ACTIVITIES = [
  [Castle, "Indoor inflatable play"], [Zap, "Movement & active games"], [Palette, "Arts & crafts"], [FlaskConical, "STEM & discovery"],
  [BookOpen, "Homework & reading support"], [Music, "Music & movement"], [Trophy, "Team challenges"], [Gamepad2, "Games"],
  [Hand, "Sensory play"], [Brush, "Creative projects"], [Library, "Story time"], [Leaf, "Quiet activities"],
  [MessagesSquare, "Social interaction"], [Star, "Confidence building"], [Moon, "Chill / reset time"],
];

const ZONES = [
  ["Basecamp", "Where every adventure begins — check-in, snacks & the day's mission", "bg-navy text-white"],
  ["Bounce Bay", "Inflatable play & big movement", "bg-turquoise text-navy"],
  ["Creator's Cave", "Arts, crafts & making", "bg-pink text-navy"],
  ["Discovery Lab", "STEM, science & curious questions", "bg-gold text-navy"],
  ["Games Arena", "Team games & friendly challenges", "bg-coral text-white"],
  ["Chill Cove", "Quiet corners to read, rest & reset", "bg-seafoam text-navy"],
  ["Explorer Fuel", "Snacks, dinners & refuelling", "bg-emerald-400 text-navy"],
];

const THRIVE = ["Move frequently", "Change activities", "Explore with their hands", "Build", "Create", "Bounce", "Ask questions", "Work in short activity blocks", "Socialise", "Have quiet reset time when needed"];

const TEAM = [
  { role: "Founder", name: "Precious Iris", icon: Compass, color: "from-gold to-coral", bio: "Created Adventure Rock to give Bermuda's busiest little explorers a place to move, create and belong." },
  { role: "Lead Educator / Program Lead", name: "Retired Teacher", icon: BookOpen, color: "from-turquoise to-seafoam", bio: "Brings years of classroom experience and gives every afternoon its structure and learning rhythm." },
  { role: "Rock Crew", name: "6 energetic program staff", icon: Users, color: "from-pink to-coral", bio: "Young, active and always ready to lead the next game, craft or challenge." },
];

const BALANCE = ["Experienced educational leadership", "Young energetic staff", "Structure", "Fun", "Creativity", "Supervision"];

const PARENT_USES = [[Briefcase, "Work"], [Stethoscope, "Attend appointments"], [ShoppingBag, "Shop"], [ListTodo, "Run errands"], [Dumbbell, "Exercise"], [HeartHandshake, "Other responsibilities"], [Coffee, "Simply breathe"]];

const HOURS = [
  [Sun, "Daytime Open Play", "9:00 AM – 2:00 PM", "Parent + Explorer time — stay on-site, work nearby and keep an eye on your child."],
  [Clock, "After-School", "3:00 PM – 6:00 PM", "Guided play, homework support and adventure every school day."],
  [Rocket, "Camps", "8:00 AM – 6:00 PM", "Full-day camps on selected school-break days."],
];

const FUTURE = ["Pre-teen programs", "Teen programs", "Leadership programs", "Teen gaming & social spaces", "STEM clubs", "Creative workshops", "School-break experiences", "Youth events", "Family activities", "Community programs"];

const VALUES = [
  [ShieldCheck, "SAFE", "Children deserve a supervised environment where parents feel confident leaving them.", "bg-turquoise/15 text-turquoise", "hover:border-turquoise"],
  [Zap, "ACTIVE", "Movement is an important part of childhood.", "bg-coral/15 text-coral", "hover:border-coral"],
  [Palette, "CREATIVE", "Children need opportunities to make, imagine and experiment.", "bg-pink/15 text-pink", "hover:border-pink"],
  [HeartHandshake, "INCLUSIVE", "Every Explorer should feel welcome.", "bg-emerald-100 text-emerald-600", "hover:border-emerald-400"],
  [ListChecks, "STRUCTURED", "Fun works even better when there is a plan.", "bg-navy/10 text-navy", "hover:border-navy"],
  [Smile, "JOYFUL", "Children should actually want to come back.", "bg-gold/20 text-amber-600", "hover:border-gold"],
];

const Wave = ({ fill, flip }) => (
  <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className={`block w-full h-10 sm:h-16 ${flip ? "rotate-180" : ""}`} aria-hidden="true">
    <path d="M0,40 C240,90 480,0 720,30 C960,60 1200,10 1440,40 L1440,80 L0,80 Z" fill={fill} />
  </svg>
);

const Eyebrow = ({ children, color = "text-turquoise" }) => <p className={`${color} font-bold tracking-[0.3em] text-xs uppercase flex items-center gap-2`}><Compass className="w-4 h-4" />{children}</p>;
const H2 = ({ children, light }) => <h2 className={`font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-tight mt-3 ${light ? "text-white" : "text-navy"}`}>{children}</h2>;

const CTAButtons = ({ items }) => (
  <div className="flex flex-wrap gap-3 mt-8">
    {items.map(([to, label, cls, id]) => (
      <Link key={to + label} to={to}><Button className={`rounded-full font-black text-sm sm:text-base px-7 py-6 transition-transform duration-200 hover:-translate-y-0.5 ${cls}`} data-testid={id}>{label} <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
    ))}
  </div>
);

function Hero() {
  return (
    <section className="relative bg-navy text-white pt-28 pb-0 overflow-hidden" data-testid="about-hero">
      <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-turquoise/20 blur-3xl" />
      <div className="absolute top-40 -left-24 w-80 h-80 rounded-full bg-pink/15 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center pb-16">
        <div>
          <motion.div {...fade}><Eyebrow color="text-gold">About Adventure Rock</Eyebrow></motion.div>
          <motion.h1 {...fade} className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-4" data-testid="about-hero-title">
            A Place Built for Kids Who Were <span className="text-turquoise">Made to Move.</span>
          </motion.h1>
          <motion.div {...fade} className="flex flex-wrap gap-2 mt-6">
            {[["Play", "bg-turquoise"], ["Explore", "bg-gold"], ["Create", "bg-pink"], ["Learn", "bg-seafoam"], ["Grow", "bg-emerald-400"]].map(([w, c], i) => (
              <motion.span key={w} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 + i * 0.1 }} className={`${c} text-navy font-black rounded-full px-4 py-1.5 text-sm`}>{w}.</motion.span>
            ))}
          </motion.div>
          <motion.p {...fade} className="text-white/75 mt-6 max-w-xl text-base sm:text-lg leading-relaxed">
            A safe, energetic and creative home for Bermuda's young explorers — at The Berkeley Cultural Center in Pembroke.
          </motion.p>
          <CTAButtons items={[["/program", "Our Programs", "bg-gold hover:bg-gold/90 text-navy", "about-hero-programs-btn"], ["/register", "Register Your Explorer", "bg-turquoise hover:bg-turquoise/90 text-navy", "about-hero-register-btn"]]} />
        </div>
        <motion.div initial={{ opacity: 0, scale: 0.95, rotate: 2 }} animate={{ opacity: 1, scale: 1, rotate: 1 }} transition={{ duration: 0.7 }} className="relative">
          <div className="absolute -inset-3 rounded-[2.5rem] bg-gradient-to-br from-turquoise via-gold to-pink opacity-80" />
          <PhotoSlot src={IMG_HERO} alt="Children playing, creating and exploring at Adventure Rock" label="Explorers in action" className="relative rounded-[2rem] w-full aspect-[4/3] shadow-2xl bg-white" testid="about-hero-image" />
          <div className="absolute -bottom-5 -left-5 bg-white text-navy rounded-2xl px-4 py-3 shadow-xl flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-turquoise" /><span className="font-bold text-sm">Supervised. Structured. Seriously fun.</span></div>
        </motion.div>
      </div>
      <Wave fill="#FFFFFF" />
    </section>
  );
}

function Story() {
  return (
    <section className="bg-white py-16 sm:py-24" data-testid="about-story">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-12 items-start">
        <motion.div {...fade} className="lg:col-span-2 relative">
          <PhotoSlot src={IMG_STORY} alt="Founder Precious Iris with explorers" label="Precious & the Explorers" className="rounded-[2rem] w-full aspect-[3/4] shadow-xl" testid="about-story-image" />
          <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full bg-gold flex items-center justify-center shadow-lg rotate-12"><span className="font-display font-black text-navy text-center text-xs leading-tight">EST.<br />2026</span></div>
        </motion.div>
        <div className="lg:col-span-3">
          <motion.div {...fade}><Eyebrow>Our Story</Eyebrow><H2>It Started With a Summer Idea.</H2></motion.div>
          <motion.div {...fade} className="mt-6 space-y-4 text-slate-600 text-base sm:text-lg leading-relaxed">
            <p>Adventure Rock Bermuda was created by <b className="text-navy">Precious Iris</b>. The idea began in the summer of 2026 as a play café designed with active, energetic and highly stimulated children in mind — including children with attention deficit hyperactivity disorder (ADHD).</p>
            <p>Precious wanted to build the kind of space she felt Bermuda's families truly needed: somewhere children could move, play, create, explore and stay engaged in a safe and welcoming environment.</p>
            <p>What began as a summer play concept quickly grew into something more. When school returned, Adventure Rock stayed open and evolved into a full after-school and camp program.</p>
            <p>Today, Adventure Rock welcomes children of <b className="text-navy">every personality, interest and energy level</b>. But its heart will always hold a special place for the child who seems to have endless energy — the one who wants to touch everything, explore everything, ask every question and is never quite ready to stop.</p>
          </motion.div>
          <motion.blockquote {...fade} className="mt-8 relative rounded-3xl bg-navy text-white p-7 sm:p-9 overflow-hidden" data-testid="about-founder-quote">
            <Quote className="absolute -top-2 -left-2 w-24 h-24 text-turquoise/20" />
            <p className="relative font-display font-bold text-xl sm:text-2xl leading-snug">“Some children need more than a chair and a worksheet after school. They need room to move, create, discover and be themselves.”</p>
            <footer className="relative mt-4 text-gold font-bold">— Precious Iris, Founder</footer>
          </motion.blockquote>
        </div>
      </div>
    </section>
  );
}

function WhyExists() {
  return (
    <section className={`${CREAM} py-16 sm:py-24`} data-testid="about-why">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fade} className="max-w-3xl"><Eyebrow color="text-coral">Why Adventure Rock Exists</Eyebrow><H2>Because Childhood Should Still Feel Like an Adventure.</H2>
          <p className="text-slate-600 mt-5 text-base sm:text-lg leading-relaxed">Precious created Adventure Rock from a genuine passion for giving children a safe place to simply enjoy being children — while also supporting the busy parents who love them.</p>
        </motion.div>
        <p className="font-display font-bold text-navy mt-10 mb-4 text-lg">Adventure Rock was created for the parent who…</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PARENT_NEEDS.map((n, i) => (
            <motion.div key={n} {...fade} transition={{ duration: 0.45, delay: i * 0.05 }} className="bg-white rounded-2xl p-5 border border-amber-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-[transform,box-shadow] duration-300">
              <span className="w-8 h-8 rounded-full bg-turquoise/15 text-turquoise font-black flex items-center justify-center text-sm">{i + 1}</span>
              <p className="text-navy font-semibold mt-3 leading-snug">{n}</p>
            </motion.div>
          ))}
        </div>
        <motion.div {...fade} className="mt-10 rounded-3xl bg-gradient-to-r from-turquoise to-seafoam p-7 sm:p-10 text-center" data-testid="about-support-statement">
          <p className="font-display font-black text-navy text-2xl sm:text-4xl">Adventure Rock supports the child <span className="bg-white/60 rounded-xl px-2">AND</span> the parent.</p>
        </motion.div>
      </div>
    </section>
  );
}

function GuidedPlay() {
  return (
    <section className="bg-white py-16 sm:py-24" data-testid="about-guided-play">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fade} className="max-w-3xl"><Eyebrow>Our Approach</Eyebrow><H2>Not Just a Room With Kids — <span className="text-turquoise">Guided Play With Purpose.</span></H2>
          <p className="text-slate-600 mt-5 text-base sm:text-lg leading-relaxed">Adventure Rock follows a structured but flexible program. Children still get to choose — but our Rock Crew builds a daily structure so every afternoon has rhythm, variety and purpose.</p>
        </motion.div>
        <div className="flex flex-wrap gap-2.5 mt-10">
          {ACTIVITIES.map(([Icon, l]) => (
            <span key={l} className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full px-4 py-2 text-sm font-semibold text-navy hover:bg-turquoise/10 hover:border-turquoise transition-colors"><Icon className="w-4 h-4 text-turquoise" />{l}</span>
          ))}
        </div>
        <p className="font-display font-bold text-navy mt-12 mb-4 text-lg">Explore the Rock</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {ZONES.map(([n, d, c], i) => (
            <motion.div key={n} {...fade} transition={{ duration: 0.45, delay: i * 0.06 }} className={`${c} rounded-3xl p-5 ${i === 0 ? "col-span-2 md:row-span-2 flex flex-col justify-end min-h-[180px]" : ""} hover:-translate-y-1 transition-transform duration-300`}>
              <p className="font-display font-black text-lg sm:text-xl">{n}</p>
              <p className="text-sm opacity-80 mt-1">{d}</p>
            </motion.div>
          ))}
        </div>
        <p className="mt-8 font-display font-black text-2xl text-coral">Adventure On!</p>
      </div>
    </section>
  );
}

function EnergyToSpare() {
  return (
    <section className={`${AQUA} py-16 sm:py-24 relative overflow-hidden`} data-testid="about-energy">
      <Sparkles className="absolute top-10 right-10 w-24 h-24 text-turquoise/20" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div {...fade}>
          <Eyebrow color="text-coral">Built for the Child Who Never Stops</Eyebrow>
          <H2>For the Child Who Has <span className="text-coral">Energy to Spare.</span></H2>
          <p className="text-slate-600 mt-5 text-base sm:text-lg leading-relaxed">Adventure Rock welcomes every child. But the space was especially inspired by children who need movement, stimulation, variety and new experiences throughout their day.</p>
          <p className="text-slate-600 mt-4 text-base sm:text-lg leading-relaxed">This way of playing and learning works for many personalities and learning styles — including some children with ADHD or other neurodiverse traits — and for any child who simply loves to be on the go.</p>
          <div className="mt-6 rounded-2xl bg-white/70 border border-turquoise/30 p-4 text-sm text-slate-600 flex gap-3" data-testid="about-not-medical-note">
            <ShieldCheck className="w-5 h-5 text-turquoise shrink-0 mt-0.5" />
            <p>Adventure Rock is a <b className="text-navy">recreational and educational program</b>. We are not a medical or therapeutic facility and do not diagnose or treat any condition.</p>
          </div>
        </motion.div>
        <motion.div {...fade} className="bg-white rounded-[2rem] p-6 sm:p-8 shadow-lg">
          <p className="font-display font-bold text-navy text-lg mb-4">Some children thrive when they can…</p>
          <div className="grid grid-cols-2 gap-3">
            {THRIVE.map((t, i) => (
              <div key={t} className="flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2.5">
                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${["bg-turquoise", "bg-gold", "bg-pink", "bg-coral", "bg-seafoam"][i % 5]}`} />
                <span className="text-sm font-semibold text-navy">{t}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Team() {
  return (
    <section className="bg-white py-16 sm:py-24" data-testid="about-team">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fade} className="max-w-3xl"><Eyebrow>Meet the Rock Team</Eyebrow><H2>Meet the People Behind the Adventure.</H2>
          <p className="text-slate-600 mt-5 text-base sm:text-lg leading-relaxed">Adventure Rock Bermuda was created by founder Precious Iris and is led by an experienced retired teacher alongside a young, energetic team of six staff members.</p>
        </motion.div>
        <div className="flex flex-wrap items-center gap-2 mt-8" data-testid="about-team-balance">
          {BALANCE.map((b, i) => (
            <span key={b} className="flex items-center gap-2">
              <span className="bg-navy text-white rounded-full px-4 py-2 text-sm font-bold">{b}</span>
              {i < BALANCE.length - 1 && <span className="text-gold font-black text-xl">+</span>}
            </span>
          ))}
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {TEAM.map((t, i) => (
            <motion.div key={t.role} {...fade} transition={{ duration: 0.5, delay: i * 0.1 }} className="group rounded-3xl border border-slate-200 bg-white overflow-hidden hover:-translate-y-2 hover:shadow-xl transition-[transform,box-shadow] duration-300" data-testid={`about-team-card-${i}`}>
              <div className={`h-44 bg-gradient-to-br ${t.color} flex items-center justify-center relative`}>
                <div className="w-24 h-24 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform duration-300"><t.icon className="w-10 h-10 text-navy" /></div>
                <span className="absolute bottom-3 right-3 text-[10px] font-bold bg-white/80 text-navy rounded-full px-2 py-0.5 flex items-center gap-1"><Camera className="w-3 h-3" />Photo coming soon</span>
              </div>
              <div className="p-6">
                <p className="text-xs font-bold text-turquoise uppercase tracking-wider">{t.role}</p>
                <p className="font-display font-black text-navy text-xl mt-1">{t.name}</p>
                <p className="text-slate-600 text-sm mt-2 leading-relaxed">{t.bio}</p>
                <div className="mt-4 space-y-1 text-xs text-slate-400">
                  <p><b className="text-slate-500">Favourite activity:</b> [coming soon]</p>
                  <p><b className="text-slate-500">Fun fact:</b> [coming soon]</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Parents() {
  return (
    <section className={`${CREAM} py-16 sm:py-24`} data-testid="about-parents">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fade}>
            <Eyebrow color="text-coral">For Busy Parents</Eyebrow><H2>We Know Parents Need Support Too.</H2>
            <p className="text-slate-600 mt-5 text-base sm:text-lg leading-relaxed">Adventure Rock is designed to fit real family life. Families use Adventure Rock while they:</p>
            <div className="flex flex-wrap gap-2 mt-5">
              {PARENT_USES.map(([Icon, l]) => <span key={l} className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 text-sm font-semibold text-navy shadow-sm"><Icon className="w-4 h-4 text-coral" />{l}</span>)}
            </div>
            <p className="text-slate-600 mt-6 leading-relaxed">During the day, our <b className="text-navy">Parent + Explorer</b> programming lets parents stay on-site, work nearby and keep an eye on their child while they play.</p>
          </motion.div>
          <motion.div {...fade}><PhotoSlot src={IMG_PARENTS} alt="A parent working nearby while children play" label="Parents & Explorers" className="rounded-[2rem] w-full aspect-[4/3] shadow-xl" testid="about-parents-image" /></motion.div>
        </div>
        <div className="grid md:grid-cols-3 gap-5 mt-12">
          {HOURS.map(([Icon, t, h, d]) => (
            <motion.div key={t} {...fade} className="bg-white rounded-3xl p-6 border border-amber-100 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-300" data-testid={`about-hours-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <Icon className="w-7 h-7 text-turquoise" />
              <p className="font-display font-black text-navy text-lg mt-3">{t}</p>
              <p className="font-display font-black text-coral text-2xl">{h}</p>
              <p className="text-slate-500 text-sm mt-2">{d}</p>
            </motion.div>
          ))}
        </div>
        <p className="text-slate-500 text-sm mt-4 flex items-center gap-2"><Bus className="w-4 h-4 text-turquoise" /> School pick-up and home drop-off may also be available.</p>
        <motion.div {...fade} className="mt-12 rounded-[2rem] bg-navy text-white p-8 sm:p-12" data-testid="about-parents-cta">
          <p className="font-display font-black text-2xl sm:text-4xl">Find the Adventure That Fits Your Family</p>
          <CTAButtons items={[["/program", "VIEW PROGRAMS", "bg-turquoise hover:bg-turquoise/90 text-navy", "about-view-programs-btn"], ["/pricing", "VIEW PRICING", "bg-white hover:bg-white/90 text-navy", "about-view-pricing-btn"], ["/register", "REGISTER YOUR EXPLORER", "bg-gold hover:bg-gold/90 text-navy", "about-register-btn"]]} />
        </motion.div>
      </div>
    </section>
  );
}

function Future() {
  return (
    <section className={`${AQUA} py-16 sm:py-24`} data-testid="about-future">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div {...fade}>
          <Eyebrow>Our Future</Eyebrow><H2>Adventure Rock Is Just Getting Started.</H2>
          <p className="text-slate-600 mt-5 text-base sm:text-lg leading-relaxed">Adventure Rock plans to grow alongside the children and families it serves.</p>
          <p className="mt-6 font-display font-bold text-navy text-xl sm:text-2xl leading-snug border-l-4 border-gold pl-5">“Our goal is to create a place children can grow with — from their first Adventure Rock experience into their pre-teen and teenage years.”</p>
        </motion.div>
        <div className="grid grid-cols-2 gap-3">
          {FUTURE.map((f, i) => (
            <motion.div key={f} {...fade} transition={{ duration: 0.4, delay: i * 0.04 }} className="bg-white rounded-2xl px-4 py-3.5 flex items-center gap-2 shadow-sm hover:shadow-md transition-shadow">
              <Rocket className="w-4 h-4 text-coral shrink-0" /><span className="font-semibold text-navy text-sm">{f}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Values() {
  return (
    <section className="bg-white py-16 sm:py-24" data-testid="about-values">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fade}><Eyebrow color="text-coral">Our Values</Eyebrow><H2>What We Stand For.</H2></motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {VALUES.map(([Icon, t, d, ic, hb], i) => (
            <motion.div key={t} {...fade} transition={{ duration: 0.45, delay: i * 0.07 }} className={`rounded-3xl border-2 border-slate-100 p-7 bg-white ${hb} hover:-translate-y-2 hover:shadow-xl transition-[transform,box-shadow,border-color] duration-300`} data-testid={`about-value-${t.toLowerCase()}`}>
              <div className={`w-14 h-14 rounded-2xl ${ic} flex items-center justify-center`}><Icon className="w-7 h-7" /></div>
              <p className="font-display font-black text-navy text-2xl mt-4 tracking-wide">{t}</p>
              <p className="text-slate-600 mt-2 leading-relaxed">{d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="relative bg-navy text-white overflow-hidden" data-testid="about-final-cta">
      <Wave fill="#FFFFFF" flip />
      {IMG_HERO && <div className="absolute inset-0 opacity-25" style={{ backgroundImage: `url(${IMG_HERO})`, backgroundSize: "cover", backgroundPosition: "center" }} />}
      <div className="absolute inset-0 bg-gradient-to-b from-navy via-navy/85 to-navy" />
      <Leaf className="absolute bottom-10 left-6 w-28 h-28 text-emerald-400/20 -rotate-12" />
      <Leaf className="absolute top-24 right-8 w-20 h-20 text-turquoise/20 rotate-45" />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-20 sm:py-28 text-center">
        <motion.h2 {...fade} className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-tight">Every Explorer Deserves a <span className="text-gold">Place to Belong.</span></motion.h2>
        <motion.p {...fade} className="text-white/80 mt-6 text-base sm:text-lg leading-relaxed">Whether your child loves to bounce, build, draw, discover, read, play games, make friends — or simply has energy that never seems to run out — there is a place for them at Adventure Rock.</motion.p>
        <div className="flex justify-center"><CTAButtons items={[["/register", "REGISTER YOUR EXPLORER", "bg-gold hover:bg-gold/90 text-navy", "about-final-register-btn"], ["/program", "EXPLORE OUR PROGRAMS", "bg-turquoise hover:bg-turquoise/90 text-navy", "about-final-programs-btn"]]} /></div>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-8 text-white/70 text-sm">
          <a href="https://www.adventurerockbermuda.com" className="flex items-center gap-2 hover:text-turquoise"><Globe className="w-4 h-4 text-turquoise" />www.adventurerockbermuda.com</a>
          <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-pink" />The Berkeley Cultural Center, Pembroke, Bermuda</span>
        </div>
      </div>
    </section>
  );
}

export default function About() {
  useEffect(() => {
    document.title = "About Adventure Rock Bermuda | After-School, Camps & Kids Activities";
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    const prev = m.content;
    m.content = "Learn the story behind Adventure Rock Bermuda, a safe, energetic and creative space for children to play, learn, explore and grow through after-school programs, camps and family activities in Pembroke, Bermuda.";
    return () => { m.content = prev; };
  }, []);
  return (
    <div className="min-h-screen overflow-x-hidden bg-white" data-testid="about-page">
      <Navbar />
      <Hero />
      <Story />
      <WhyExists />
      <GuidedPlay />
      <EnergyToSpare />
      <Team />
      <Parents />
      <Future />
      <Values />
      <FinalCTA />
      <Footer />
    </div>
  );
}
